// Universal Event Manager assignment section (Feb 2026).
//
// One reusable panel for the Admin manage/edit surface across every event
// type — event_session, tournament, round_robin, club_event.
//
// Product rule enforced here + on the backend:
//   • ONE Event Manager per event
//   • Admin-only: Assign / Change / Remove
//   • Event Manager themselves can NOT reach this UI
//   • Only ACTIVE users are pickable
//
// The `Assign` button toggles to `Change` once someone is assigned, so the
// admin's mental model stays "who is the manager for this event?" — never
// a growing list.

import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { adminAPI } from '../../lib/api';
import { ShieldCheck, X, User as UserIcon } from '@phosphor-icons/react';
import ConfirmDialog from './ConfirmDialog';

/**
 * @param {object}  props
 * @param {'event_session'|'tournament'|'round_robin'|'club_event'} props.eventKind
 * @param {string}  props.eventId
 * @param {boolean} props.isAdmin  Only global admins render this panel.
 */
export default function EventManagerAssignment({ eventKind, eventId, isAdmin }) {
  const [managers, setManagers] = useState([]); // [{id, profile_id, name, account_status}]
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null); // {profile_id, name}
  const [removing, setRemoving] = useState(false);

  const load = useCallback(async () => {
    if (!eventKind || !eventId) return;
    try {
      const r = await adminAPI.getEventManager(eventKind, eventId);
      const list = r.data?.managers || (r.data?.manager ? [r.data.manager] : []);
      setManagers(list);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load Event Managers.');
    } finally {
      setLoading(false);
    }
  }, [eventKind, eventId]);

  useEffect(() => {
    if (isAdmin) load();
    else setLoading(false);
  }, [isAdmin, load]);

  if (!isAdmin) return null;

  const handleRemove = async () => {
    if (!confirmRemove) return;
    setRemoving(true);
    try {
      await adminAPI.removeOneEventManager(eventKind, eventId, confirmRemove.profile_id);
      toast.success(`${confirmRemove.name} is no longer an Event Manager.`);
      setConfirmRemove(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove.');
    } finally {
      setRemoving(false);
    }
  };

  const isInactive = (m) => {
    const s = (m.account_status || '').toLowerCase();
    return s && !['approved', 'active'].includes(s);
  };

  return (
    <section
      className="bg-[#fbfaf8] border border-[#e3dcd1] rounded-2xl p-5 space-y-3"
      data-testid="event-manager-section"
    >
      <header className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="shrink-0 w-8 h-8 rounded-lg bg-[#015e3f]/10 text-[#015e3f] flex items-center justify-center">
            <ShieldCheck size={16} weight="fill" />
          </div>
          <div className="min-w-0">
            <h2 className="font-serif text-lg text-[#013523]">Event Managers</h2>
            <p className="text-xs text-[#6a766e]">
              Grant active members full operational access to this event only. Add as many as you need.
            </p>
          </div>
        </div>
        {!loading && (
          <button
            type="button"
            data-testid="event-manager-assign-btn"
            onClick={() => setPickerOpen(true)}
            className="shrink-0 inline-flex items-center gap-1 bg-[#015e3f] hover:bg-[#014d34] text-white text-xs font-medium rounded-full px-3 py-1.5"
          >
            + Add Event Manager
          </button>
        )}
      </header>

      {loading ? (
        <div className="h-10 bg-[#f0ece3] rounded animate-pulse" />
      ) : managers.length === 0 ? (
        <p className="text-xs text-[#6a766e]" data-testid="event-manager-empty">
          No Event Managers assigned yet.
        </p>
      ) : (
        <ul className="space-y-1.5" data-testid="event-manager-list">
          {managers.map((m) => (
            <li
              key={m.id || m.profile_id}
              data-testid={`event-manager-row-${m.profile_id}`}
              className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-white border border-[#e3dcd1]"
            >
              <div className="flex items-center gap-2 min-w-0">
                <UserIcon size={14} className="text-[#6a766e] shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm text-[#013523] truncate">{m.name}</div>
                  {isInactive(m) && (
                    <div className="text-[10px] uppercase tracking-wider text-[#a04949] mt-0.5"
                         data-testid={`event-manager-inactive-badge-${m.profile_id}`}>
                      Inactive — cannot exercise Event Manager rights
                    </div>
                  )}
                </div>
              </div>
              <button
                type="button"
                data-testid={`event-manager-remove-${m.profile_id}`}
                aria-label={`Remove ${m.name}`}
                onClick={() => setConfirmRemove({ profile_id: m.profile_id, name: m.name })}
                className="shrink-0 text-[11px] font-medium text-[#7a3a3a] hover:underline px-2 py-1"
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      {pickerOpen && (
        <ManagerPicker
          eventKind={eventKind}
          eventId={eventId}
          existingProfileIds={new Set(managers.map((m) => m.profile_id))}
          onAssigned={async () => { setPickerOpen(false); await load(); }}
          onClose={() => setPickerOpen(false)}
        />
      )}

      <ConfirmDialog
        open={!!confirmRemove}
        onOpenChange={(v) => !v && setConfirmRemove(null)}
        title="Remove Event Manager?"
        description={confirmRemove ? `${confirmRemove.name} will lose management rights for this event. Other managers are unaffected.` : ''}
        confirmLabel="Remove"
        destructive
        busy={removing}
        onConfirm={handleRemove}
      />
    </section>
  );
}

function ManagerPicker({ eventKind, eventId, existingProfileIds, onAssigned, onClose }) {
  const [candidates, setCandidates] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(null);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    adminAPI.listEventManagerCandidates(q)
      .then((r) => { if (!cancel) setCandidates(r.data?.candidates || []); })
      .catch((err) => { if (!cancel) toast.error(err.response?.data?.detail || 'Failed to load members.'); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [q]);

  const pick = async (p) => {
    if (assigning) return;
    setAssigning(p.profile_id);
    try {
      const r = await adminAPI.assignEventManager(eventKind, eventId, p.profile_id);
      if (r.data?.idempotent) {
        toast.info(`${p.name} is already an Event Manager for this event.`);
      } else {
        toast.success(`${p.name} is now an Event Manager.`);
      }
      onAssigned?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign.');
    } finally {
      setAssigning(null);
    }
  };

  const filtered = candidates.filter((c) => !existingProfileIds.has(c.profile_id));

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4"
         data-testid="event-manager-picker">
      <div className="absolute inset-0 bg-black/55 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl border border-[#e3dcd1] overflow-hidden">
        <div className="px-4 py-3 border-b border-[#e3dcd1] flex items-center justify-between">
          <h3 className="font-serif text-lg text-[#013523]">Add Event Manager</h3>
          <button type="button" aria-label="Close" onClick={onClose}
                  className="text-[#6a766e] hover:text-[#013523] p-1">
            <X size={16} weight="bold" />
          </button>
        </div>
        <div className="p-3 space-y-2">
          <input
            data-testid="event-manager-picker-search"
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search active members…"
            className="w-full px-3 py-2 text-sm bg-[#fbfaf8] border border-[#e3dcd1] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#015e3f]/30"
          />
          <div className="max-h-72 overflow-y-auto -mx-3 px-3"
               data-testid="event-manager-picker-list">
            {loading ? (
              <div className="text-xs text-[#6a766e] py-3">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="text-xs text-[#6a766e] py-3">No matching active members.</div>
            ) : (
              <ul className="space-y-1">
                {filtered.map((p) => (
                  <li key={p.profile_id}>
                    <button
                      type="button"
                      data-testid={`event-manager-pick-${p.profile_id}`}
                      disabled={assigning === p.profile_id}
                      onClick={() => pick(p)}
                      className="w-full text-left px-3 py-2 rounded-lg hover:bg-[#f0ece3]/60 text-sm text-[#013523] disabled:opacity-50"
                    >
                      {p.name}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
